import { useState, useRef, useEffect, useContext } from 'react'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Button from 'react-bootstrap/Button'
import ButtonGroup from 'react-bootstrap/ButtonGroup'
import Card from 'react-bootstrap/Card'
import Spinner from 'react-bootstrap/Spinner'
import Alert from 'react-bootstrap/Alert'
import Form from 'react-bootstrap/Form'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Modal from 'react-bootstrap/Modal'
import { faArrowLeft, faExclamationTriangle, faPen, faTrash } from '@fortawesome/free-solid-svg-icons'
import ProgressBar from 'react-bootstrap/ProgressBar'
import UserContext from 'context/user-context'
import { useReport, useUpdateReport, useDeleteReport } from 'api/hooks/use-reports'
import { calculatePercent } from 'helpers/utils'
import { useQueryClient } from 'react-query'
import TransactionTable from 'transactions/table'
import AddAttribute from 'reports/show/features/create'
import DownloadTransactions from 'transactions/table/actions'
import ShareButton from 'shared/buttons/share-button'
import { useCreateAuditReportUser } from 'api/hooks/use-audit-report-user'
import ReportUserRow from 'reports/user-row'

const SampleDetail = ({ sampleId, onBack, dashboardId, backLabel = 'Back to Samples', cardHeader = false }) => {
  const user = useContext(UserContext)
  const { municipality } = user
  const queryClient = useQueryClient()
  const [isEditing, setIsEditing] = useState(false)
  const [editName, setEditName] = useState('')
  const [showProgress, setShowProgress] = useState(true)
  const [progressView, setProgressView] = useState('completion')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteConfirmName, setDeleteConfirmName] = useState('')
  const inputRef = useRef(null)

  const options = {
    refetchInterval: (data) => {
      return data?.status === 'generating' ? 1000 : false
    }
  }

  const { isSuccess, isLoading, data: report } = useReport({ id: sampleId, options })

  // Use the prop if provided, otherwise fall back to the report's dashboard association
  const effectiveDashboardId = dashboardId || (isSuccess ? report.dashboard_id : null)

  const isOwner = isSuccess && report.user_id === user.id
  const isDirectEditor = isSuccess && report.shared_users?.find(
    (su) => su.user_id === user.id
  )?.is_editor
  const isDashboardEditor = isSuccess && report.is_dashboard_editor
  const isFolderEditor = isSuccess && report.is_folder_editor
  const canEdit = isOwner || isDirectEditor || isDashboardEditor || isFolderEditor

  const { mutate: updateName } = useUpdateReport({
    id: sampleId,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries(`reports/${sampleId}`)
        if (effectiveDashboardId) {
          queryClient.invalidateQueries(['dashboard-audit-reports', effectiveDashboardId])
        }
        setIsEditing(false)
      }
    }
  })

  const { mutate: deleteSample } = useDeleteReport({
    id: sampleId,
    options: {
      onSuccess: () => {
        if (effectiveDashboardId) {
          queryClient.invalidateQueries(['dashboard-audit-reports', effectiveDashboardId])
        }
        setShowDeleteModal(false)
        onBack()
      }
    }
  })

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleStartEditing = () => {
    if (!canEdit) return
    setEditName(report.name)
    setIsEditing(true)
  }

  const handleSave = () => {
    const trimmed = editName.trim()
    if (trimmed && trimmed !== report.name) {
      updateName({ name: trimmed })
    } else {
      setIsEditing(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave()
    if (e.key === 'Escape') setIsEditing(false)
  }

  const headerContent = (
    <Row className={`${cardHeader ? '' : 'mb-3 '}align-items-center`}>
      {onBack && (
        <Col sm="auto">
          <Button variant="light" size="sm" onClick={onBack}>
            <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
            {backLabel}
          </Button>
        </Col>
      )}
      {isSuccess && (
        <Col>
          {isEditing ? (
            <Form.Control
              ref={inputRef}
              type="text"
              size="sm"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onBlur={handleSave}
              onKeyDown={handleKeyDown}
            />
          ) : (
            <h5
              className="mb-0 d-inline-flex align-items-center gap-2"
              style={{ cursor: canEdit ? 'pointer' : 'default' }}
              onClick={handleStartEditing}
            >
              {report.name}
              {canEdit && (
                <FontAwesomeIcon icon={faPen} className="text-muted" style={{ fontSize: '0.65em' }} />
              )}
            </h5>
          )}
        </Col>
      )}
      {isSuccess && (
        <Col sm="auto" className="d-flex align-items-center gap-2">
          {isOwner && (
            <ShareButton
              resource={report}
              resourceName={report.name}
              useCreateMutation={useCreateAuditReportUser}
              createParams={{ audit_report_id: report.id }}
              queryKeys={[`reports/${report.id}`, 'reports']}
              UserRowComponent={ReportUserRow}
              userRowProps={(u) => ({ auditReportUser: u, editable: report.user_id === user.id })}
              buttonText="Share"
            />
          )}
          {canEdit && (
            <AddAttribute reportId={report.id} reportFeatures={report.features} />
          )}
          <DownloadTransactions
            defaultName={report.name}
            source={report.name}
            queryParams={{
              audit_report_id: String(sampleId),
              ...(effectiveDashboardId ? { dashboard_id: String(effectiveDashboardId) } : {}),
              section_id: report.section_id
            }}
          />
          {isOwner && (
            <Button
              variant="light"
              size="sm"
              className="text-danger"
              onClick={() => setShowDeleteModal(true)}
            >
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          )}
        </Col>
      )}
    </Row>
  )

  return (
    <>
      {cardHeader ? (
        <Card className="mb-3">
          <Card.Body>{headerContent}</Card.Body>
        </Card>
      ) : (
        headerContent
      )}

      {isLoading && (
        <div className="text-center py-5">
          <Spinner animation="grow" variant="warning" />
        </div>
      )}

      {isSuccess && !canEdit && (
        <Card
          className="bg-warning border-warning mb-3"
          style={{ '--bs-bg-opacity': 0.2 }}
        >
          <Card.Body className="py-2">
            <div className="d-flex align-items-center">
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="text-warning me-2"
              />
              <div className="text-dark">
                <strong>Viewer Mode:</strong> You cannot edit this sample.
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {isSuccess && (
        <Row className="mt-2">
          {report.dashboard && report.filters_mismatch_reasons?.length > 0 && (
            <Col sm={12} className="mb-3">
              {report.filters_mismatch_reasons?.includes('filter_criteria') && (
                <Alert variant="warning" className="py-2 mb-0">
                  <small>
                    <FontAwesomeIcon
                      className="text-warning"
                      icon={faExclamationTriangle}
                    />{' '}
                    The dashboard&apos;s filters have changed since this sample was created. Some transactions may no longer match the current filter criteria.
                  </small>
                </Alert>
              )}
              {report.filters_mismatch_reasons?.includes('analytic_configs') && (
                <Alert variant="warning" className="py-2 mb-0 mt-2">
                  <small>
                    <FontAwesomeIcon
                      className="text-warning"
                      icon={faExclamationTriangle}
                    />{' '}
                    One or more analytic configurations have been updated since this sample was created. Transaction flagging may differ from current results.
                  </small>
                </Alert>
              )}
            </Col>
          )}
          {report.features.length === 0 && (
            <Col sm={12} className="mb-3">
              <Alert variant="warning" className="py-2 mb-0">
                <small>
                  <FontAwesomeIcon
                    className="text-warning"
                    icon={faExclamationTriangle}
                  />{' '}
                  No Additional Testing Attributes added
                </small>
              </Alert>
            </Col>
          )}
          <Col>
            <Card className="shadow-sm">
              <Card.Body className="py-2">
                <div className="d-flex align-items-center justify-content-between">
                  <small
                    className="text-muted fw-semibold"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setShowProgress(!showProgress)}
                  >
                    {showProgress ? 'Hide Progress' : 'Show Progress'}
                  </small>
                  {showProgress && (
                    <ButtonGroup size="sm">
                      <Button
                        variant={progressView === 'completion' ? 'primary' : 'outline-primary'}
                        onClick={() => setProgressView('completion')}
                      >
                        Completion
                      </Button>
                      <Button
                        variant={progressView === 'results' ? 'primary' : 'outline-primary'}
                        onClick={() => setProgressView('results')}
                      >
                        Results
                      </Button>
                    </ButtonGroup>
                  )}
                </div>
                {showProgress && (
                  <table className="table table-sm table-bordered mt-3 mb-0">
                    <tbody>
                      <tr>
                        <td style={{ width: '35%' }}>
                          <small className="fw-semibold">Overall</small>
                        </td>
                        <td className="align-middle text-center" style={{ width: '80px' }}>
                          <small className="fw-semibold">
                            {calculatePercent(report.complete_count, report.total_count)}%
                          </small>
                        </td>
                        <td className="align-middle">
                          {progressView === 'completion' ? (
                            <ProgressBar
                              now={calculatePercent(report.complete_count, report.total_count)}
                              style={{ height: '10px' }}
                            />
                          ) : (
                            <ProgressBar style={{ height: '10px' }}>
                              <ProgressBar variant="warning" now={calculatePercent(report.in_progress_count, report.total_count)} key="in-progress" />
                              <ProgressBar variant="danger" now={calculatePercent(report.exception_count, report.total_count)} key="exception" />
                              <ProgressBar variant="success" now={calculatePercent(report.no_exception_count, report.total_count)} key="no-exception" />
                              <ProgressBar variant="light" now={calculatePercent(report.open_count, report.total_count)} key="open" />
                            </ProgressBar>
                          )}
                        </td>
                      </tr>
                      {(report.features || []).map((feature) => {
                        const pct = calculatePercent(feature.complete_count, feature.total_count)
                        return (
                          <tr key={feature.key}>
                            <td style={{ width: '35%' }}>
                              <small className="fw-semibold">{feature.name} ({municipality?.abbreviation}-{feature.key})</small>
                              {feature.description && (
                                <br />
                              )}
                              {feature.description && (
                                <small className="text-muted">{feature.description}</small>
                              )}
                            </td>
                            <td className="align-middle text-center" style={{ width: '80px' }}>
                              <small className="fw-semibold">{pct}%</small>
                            </td>
                            <td className="align-middle">
                              {progressView === 'completion' ? (
                                <ProgressBar
                                  now={pct}
                                  style={{ height: '10px' }}
                                />
                              ) : (
                                <ProgressBar style={{ height: '10px' }}>
                                  <ProgressBar variant="warning" now={calculatePercent(feature.in_progress_count, feature.total_count)} key="in-progress" />
                                  <ProgressBar variant="danger" now={calculatePercent(feature.exception_count, feature.total_count)} key="exception" />
                                  <ProgressBar variant="success" now={calculatePercent(feature.no_exception_count, feature.total_count)} key="no-exception" />
                                  <ProgressBar variant="light" now={calculatePercent(feature.open_count, feature.total_count)} key="open" />
                                </ProgressBar>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                )}
              </Card.Body>
              <Card.Body>
                {report.status === 'generating' && (
                  <>
                    <Spinner animation="border" variant="primary" size="sm" />{' '}
                    Generating sample...
                  </>
                )}

                {report.status === 'available' && (
                  <TransactionTable
                    title=""
                    source={report.name}
                    report={report}
                    sectionId={report.section_id}
                    orderBy="report_analytics_count"
                    columns={[
                      'processing_modal',
                      'risk_score',
                      'single_status',
                      ...(report.features || []).map(f => `feature_review:${f.key}`)
                    ]}
                    auditReportId={String(sampleId)}
                    dashboardId={effectiveDashboardId ? String(effectiveDashboardId) : ''}
                    reportFeatures={report.features}
                    filtersEnabled={false}
                    downloadButton={false}
                    showCustomColumns={false}
                    showAddToListColumn={false}
                    canEdit={canEdit}
                  />
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {isSuccess && isOwner && (
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Delete Sample</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              This will permanently delete <strong>{report.name}</strong> and all
              associated data. This action cannot be undone.
            </p>
            <Form.Group>
              <Form.Label className="text-muted">
                Type <strong>{report.name}</strong> to confirm
              </Form.Label>
              <Form.Control
                type="text"
                value={deleteConfirmName}
                onChange={(e) => setDeleteConfirmName(e.target.value)}
                placeholder={report.name}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={deleteConfirmName !== report.name}
              onClick={() => deleteSample()}
            >
              Delete Sample
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  )
}

export default SampleDetail
